import {Button} from "../../../ui/button";
import {Bleed, Grid, HStack, Icon, Separator, Stack, Text} from "@chakra-ui/react";
import {AdjustmentsHorizontalIcon} from "@heroicons/react/24/solid";
import {PopoverContent, PopoverRoot, PopoverTrigger} from "../../../ui/popover";
import {typedEntries, typedMemo} from "../../../../utils/typescript";
import {useMemo, useRef} from "react";
import {OrderingSelector} from "./OrderingSelector";
import {ColumnEditor} from "./ColumnEditor";
import {BottomBar} from "./BottomBar";
import {
  ExtractTableContext,
  isColumnGroupable,
  isColumnSortable,
  TableConfig,
  TableState,
} from "../../../../hooks/table";
import {BarsArrowDownIcon} from "@heroicons/react/16/solid";
import {GroupingSelectors} from "./GroupingSelectors";
import T from "platformed-components/i18n/T";

export const TableDisplayMenu = typedMemo(
  <C extends TableConfig>({
    config,
    context,
    state,
    buttonVariant = "ghost",
  }: {
    config: C;
    context: ExtractTableContext<C>;
    state: TableState<C>;
    buttonVariant?: "ghost" | "outline";
  }) => {
    const popoverContentRef = useRef<HTMLDivElement>(null);

    const canGroup = useMemo(
      () => typedEntries(config.columns).some(([_, column]) => isColumnGroupable(column)),
      [config.columns],
    );
    const canSort = useMemo(
      () => typedEntries(config.columns).some(([_, column]) => isColumnSortable(column)),
      [config.columns],
    );

    return (
      <PopoverRoot positioning={{placement: "bottom-end"}}>
        <PopoverTrigger asChild>
          <Button size="xs" variant={buttonVariant}>
            <Icon>
              <AdjustmentsHorizontalIcon />
            </Icon>
            <T keyName="c.Display" />
          </Button>
        </PopoverTrigger>
        <PopoverContent ref={popoverContentRef} minWidth="420px" overflow="hidden">
          <Stack gap={0} separator={<Separator />} overflow="hidden">
            <Stack gap={0} separator={<Separator />} overflowY="auto" flex="1 1 auto">
              {(canGroup || canSort) && (
                <Grid templateColumns="1fr 1.6fr" p={4} rowGap={2} alignItems="center">
                  {canGroup && (
                    <GroupingSelectors
                      config={config}
                      context={context}
                      groupState={state.groupState}
                      onGroupStateChange={state.setGroupState}
                      portalRef={popoverContentRef}
                    />
                  )}
                  {canSort && (
                    <>
                      <HStack color="fg.muted">
                        <Icon boxSize="1em">
                          <BarsArrowDownIcon />
                        </Icon>
                        <Text fontWeight="medium">
                          <T keyName="c.Ordering" />
                        </Text>
                      </HStack>
                      <OrderingSelector
                        config={config}
                        context={context}
                        sortState={state.sortState}
                        onSortStateChange={state.setSortState}
                        portalRef={popoverContentRef}
                        containerProps={{flex: "1 1 auto"}}
                      />
                    </>
                  )}
                </Grid>
              )}
              <Stack p={4}>
                <Text fontWeight="medium">
                  <T keyName="c.Columns" />
                </Text>
                <Bleed inline="3">
                  <ColumnEditor
                    config={config}
                    context={context}
                    columnOrder={state.columnOrder}
                    visibleColumns={state.visibleColumns}
                    setColumnPosition={state.setColumnPosition}
                    setColumnVisible={state.setColumnVisible}
                  />
                </Bleed>
              </Stack>
            </Stack>
            {state.isChangedFromDefault && <BottomBar reset={state.reset} />}
          </Stack>
        </PopoverContent>
      </PopoverRoot>
    );
  },
);
